import { useState, useEffect } from "react"
import { useSaleCreateStore } from "../../stores/useSaleCreateStore"

export const BasicInfoStep = () => {
  const { saleData, updateBasicInfo, updateScheduling } = useSaleCreateStore()

  const [formData, setFormData] = useState({
    name: saleData.name || "",
    location_text: saleData.location_text || "",
    description: saleData.description || "",
    sale_type: saleData.sale_type || "lump sum",
  })

  const [scheduling, setScheduling] = useState({
    bid_start_datetime: saleData.bid_start_datetime
      ? formatDatetimeLocal(saleData.bid_start_datetime)
      : "",
    bid_stop_datetime: saleData.bid_stop_datetime
      ? formatDatetimeLocal(saleData.bid_stop_datetime)
      : "",
    bid_select_datetime: saleData.bid_select_datetime
      ? formatDatetimeLocal(saleData.bid_select_datetime)
      : "",
  })

  // Helper function to format datetime for input field
  function formatDatetimeLocal(date: Date | string | undefined): string {
    if (!date) return ""

    const dateObj = typeof date === "string" ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ""

    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, "0")
    const day = String(dateObj.getDate()).padStart(2, "0")
    const hours = String(dateObj.getHours()).padStart(2, "0")
    const minutes = String(dateObj.getMinutes()).padStart(2, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Update store whenever form data changes
  useEffect(() => {
    updateBasicInfo(formData)
  }, [formData, updateBasicInfo])

  // Update store whenever scheduling changes
  useEffect(() => {
    const schedulingData = {
      bid_start_datetime: scheduling.bid_start_datetime
        ? new Date(scheduling.bid_start_datetime)
        : undefined,
      bid_stop_datetime: scheduling.bid_stop_datetime
        ? new Date(scheduling.bid_stop_datetime)
        : undefined,
      bid_select_datetime: scheduling.bid_select_datetime
        ? new Date(scheduling.bid_select_datetime)
        : undefined,
    }
    updateScheduling(schedulingData)
  }, [scheduling, updateScheduling])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSchedulingChange = (field: string, value: string) => {
    setScheduling((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div className="space-y-6">
      {/* Sale Name */}
      <div>
        <label
          htmlFor="sale-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Sale Name *
        </label>
        <input
          type="text"
          id="sale-name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter a descriptive name for the sale"
          required
        />
        <p className="text-sm text-gray-500 mt-1">
          This will be the main identifier for your timber sale
        </p>
      </div>

      {/* Location */}
      <div>
        <label
          htmlFor="location"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Location *
        </label>
        <input
          type="text"
          id="location"
          value={formData.location_text}
          onChange={(e) => handleInputChange("location_text", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., Valdosta, GA"
          required
        />
        <p className="text-sm text-gray-500 mt-1">
          City, state or other location description
        </p>
      </div>

      {/* Sale Type */}
      <div>
        <label
          htmlFor="sale-type"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Sale Type *
        </label>
        <select
          id="sale-type"
          value={formData.sale_type}
          onChange={(e) =>
            handleInputChange(
              "sale_type",
              e.target.value as "pay as cut" | "lump sum"
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="lump sum">Lump Sum</option>
          <option value="pay as cut">Pay As Cut</option>
        </select>
        <p className="text-sm text-gray-500 mt-1">
          {formData.sale_type === "lump sum"
            ? "Buyers submit a single total bid amount for all timber products"
            : "Buyers submit per-unit prices for each timber product type"}
        </p>
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Description
        </label>
        <textarea
          id="description"
          rows={4}
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          placeholder="Provide additional details about the timber sale, such as tree species, age, access roads, environmental considerations, etc."
        />
        <p className="text-sm text-gray-500 mt-1">
          Optional description to help bidders understand the sale details
        </p>
      </div>

      {/* Bidding Schedule Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Bidding Schedule
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Bid Start */}
          <div>
            <label
              htmlFor="bid-start"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Bidding Opens *
            </label>
            <input
              type="datetime-local"
              id="bid-start"
              value={scheduling.bid_start_datetime}
              onChange={(e) =>
                handleSchedulingChange("bid_start_datetime", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Bid Stop */}
          <div>
            <label
              htmlFor="bid-stop"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Bidding Closes *
            </label>
            <input
              type="datetime-local"
              id="bid-stop"
              value={scheduling.bid_stop_datetime}
              onChange={(e) =>
                handleSchedulingChange("bid_stop_datetime", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Bid Selection */}
          <div>
            <label
              htmlFor="bid-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Bid Selection Date *
            </label>
            <input
              type="datetime-local"
              id="bid-select"
              value={scheduling.bid_select_datetime}
              onChange={(e) =>
                handleSchedulingChange("bid_select_datetime", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Set when bidding opens, closes, and when you plan to select the
          winning bid
        </p>
      </div>
    </div>
  )
}
